<template>
  <div class="d-page-wrapper d-notifications-page">
    <div class="d-page-toolbar">
      <d-search-input
        v-model="search"
        @submit="onSearchSubmit"
      ></d-search-input>
      <v-checkbox
        v-model="showOnlyWithoutReminder"
        class="ml-3 mt-0 pt-0"
        hide-details
        dense
        :label="$t('notification.onlyWithoutReminder')"
      ></v-checkbox>
      <v-btn
        class="d-btn-1 ml-3 d-notification-reminder-actions"
        depressed
        :loading="isSendingReminders"
        :disabled="!selectedNotifications.length || isSendingReminders"
        @click="sendReminderSMS"
      >
        {{ $t('notification.sendReminders') }}
      </v-btn>
    </div>

    <v-data-table
      v-model="selectedNotifications"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :headers="tableHeaders"
      :items="tableItems"
      :page="pageNumber"
      :items-per-page="pageSize"
      :server-items-length="totalCount"
      item-key="id"
      show-select
      must-sort
      hide-default-footer
      class="d-data-table d-data-table--rows-clickable"
      @page-count="pageCount = $event"
      @click:row="openViewDialog"
    >
      <template v-slot:[`item.licenseNumber`]="{ item }">
        <v-icon class="d-data-table-cell-icon">mdi-flag</v-icon>
        {{ item.licenseNumber }}
      </template>
      <template v-slot:[`item.sentDateTimeUtc`]="{ item }">
        {{ dateTimeUtil.utcToLocalDisplayDateTimeFormat(item.sentDateTimeUtc) }}
      </template>
      <template v-slot:[`item.reminderDate`]="{ item }">
        <template v-if="item.reminderDate">
          <d-check-icon :value="true" class="mr-1"></d-check-icon>
          {{ dateTimeUtil.utcToLocalDisplayDateTimeFormat(item.reminderDate) }}
        </template>
      </template>
      <template v-slot:[`item.notificationTypeId`]="{ item }">
        {{ (getNotificationTypeById(item.notificationTypeId) || {}).name }}
      </template>
      <template v-slot:[`item.dealerId`]="{ item }">
        {{ (shortDealerById(item.dealerId) || {}).name }}
      </template>
    </v-data-table>

    <v-pagination
      v-model="pageNumber"
      :length="pageCount"
      :total-visible="15"
      class="my-5"
      @input="onPageInput"
    ></v-pagination>

    <notification-view-dialog
      :dialog.sync="viewDialog"
      :notification="viewDialog_notification"
    ></notification-view-dialog>
  </div>
</template>


<script>
import { mapGetters, mapActions } from 'vuex'
import { notificationsPageSize } from '@/constants'

import dateTimeUtil from '@/utils/dateTime.util'

import pageMixin from '@/mixins/page.mixin'
import snackbarMixin from '@/mixins/snackbar.mixin'
import notificationTypeMixin from '@/mixins/notificationType.mixin'

import DCheckIcon from '@/components/DCheckIcon'
import DSearchInput from '@/components/DSearchInput'
import NotificationViewDialog from './NotificationViewDialog'

const reminderCooldownDays = 7
const millisecondsPerDay = 24 * 60 * 60 * 1000

const sortByStorageKey = 'notificationsSortBy'
const sortDescStorageKey = 'notificationsSortDesc'
const defaultSortBy = 'sentDateTimeUtc'

const restoreSortBy = () => {
  return localStorage.getItem(sortByStorageKey) || defaultSortBy
}

const restoreSortDesc = () => {
  const stored = localStorage.getItem(sortDescStorageKey)
  return stored === null ? true : stored === 'true'
}


export default {
  components: {
    DCheckIcon,
    DSearchInput,
    NotificationViewDialog
  },


  mixins: [
    pageMixin,
    snackbarMixin,
    notificationTypeMixin
  ],


  data () {
    return {
      dateTimeUtil,
      sortBy: restoreSortBy(),
      sortDesc: restoreSortDesc(),
      skipSortReload: true,
      search: null,
      pageCount: 0,
      pageNumber: 1,
      pageSize: notificationsPageSize,
      showOnlyWithoutReminder: false,
      selectedNotifications: [],
      isSendingReminders: false,
      viewDialog: false,
      viewDialog_notification: null
    }
  },


  computed: {
    ...mapGetters('user', ['currentUserIsChainAdmin', 'currentUser']),
    ...mapGetters('chain', ['currentChainId']),
    ...mapGetters('dealer', ['currentDealerId', 'previousDealerId', 'shortDealerById']),
    ...mapGetters('notification', ['notificationsTableItems', 'notificationsTotalCount']),

    tableHeaders () {
      const tableHeaders = [
        { text: this.$t('notification.table.licenseNumber'), value: 'licenseNumber', cellClass: 'font-weight-medium text-no-wrap' },
        { text: this.$t('notification.table.vinNumber'), value: 'vinNumber', cellClass: 'font-weight-medium text-no-wrap' },
        { text: this.$t('notification.table.type'), value: 'notificationTypeId' },
        { text: this.$t('notification.table.sentDateTime'), value: 'sentDateTimeUtc', cellClass: 'text-no-wrap' },
        { text: this.$t('notification.table.reminderDate'), value: 'reminderDate', cellClass: 'text-no-wrap' },
        { text: this.$t('notification.table.subject'), value: 'subject', cellClass: 'd-overflow-ellipsis d-notifications-table-cell-subject' },
        { text: this.$t('notification.table.message'), value: 'message', cellClass: 'd-overflow-ellipsis d-notifications-table-cell-message' }
      ]

      if (this.currentDealerId === '') {
        tableHeaders.push(
          { text: this.$t('notification.table.dealer'), value: 'dealerId', sortable: false, cellClass: 'd-overflow-ellipsis d-notifications-table-cell-message' }
        )
      }
      return tableHeaders
    },

    tableItems () {
      const items = this.notificationsTableItems(this.currentDealerId)

      if (!this.showOnlyWithoutReminder) {
        return items
      }

      return items.filter(item => !item.reminderDate)
    },

    totalCount () {
      if (this.showOnlyWithoutReminder) {
        return this.tableItems.length
      }

      return this.notificationsTotalCount(this.currentDealerId)
    },

    chainId () {
      return this.currentUserIsChainAdmin ? this.currentChainId : this.currentUser.chainId
    },

    sortQueryKey () {
      const sortBy = Array.isArray(this.sortBy) ? this.sortBy[0] : this.sortBy
      const sortDesc = Array.isArray(this.sortDesc) ? this.sortDesc[0] : this.sortDesc
      return `${sortBy || defaultSortBy}:${sortDesc ? 'desc' : 'asc'}`
    }
  },


  watch: {
    async currentDealerId () {
      this.restoreInitialNotificationsTableItems(this.previousDealerId)
      this.search = null
      await this.reloadTableItems(1, true)
    },
    async currentChainId () {
      this.restoreInitialNotificationsTableItems(this.previousDealerId)
      this.search = null
      await this.reloadTableItems(1, true)
    },

    showOnlyWithoutReminder () {
      this.pageNumber = 1
      this.selectedNotifications = []
    },

    sortQueryKey () {
      const [sortBy, sortOrder] = this.sortQueryKey.split(':')
      localStorage.setItem(sortByStorageKey, sortBy)
      localStorage.setItem(sortDescStorageKey, String(sortOrder === 'desc'))

      if (this.skipSortReload) return
      this.reloadTableItems(1)
    }
  },


  methods: {
    ...mapActions('notification', ['loadNotificationsTableItems', 'loadNotificationsByVehicle', 'restoreInitialNotificationsTableItems', 'sendNotificationsReminders']),

    isReminderDateInCooldown (reminderDate) {
      if (!reminderDate) {
        return false
      }

      const reminderDateTimestamp = new Date(reminderDate).getTime()

      if (Number.isNaN(reminderDateTimestamp)) {
        return false
      }

      const nowTimestamp = Date.now()
      return (nowTimestamp - reminderDateTimestamp) < (reminderCooldownDays * millisecondsPerDay)
    },

    getLatestReminderDate (notifications) {
      return notifications.reduce((latest, notification) => {
        if (!notification.reminderDate) {
          return latest
        }

        const timestamp = new Date(notification.reminderDate).getTime()

        if (Number.isNaN(timestamp)) {
          return latest
        }

        if (!latest || timestamp > latest.timestamp) {
          return {
            timestamp,
            reminderDate: notification.reminderDate
          }
        }

        return latest
      }, null)?.reminderDate || null
    },

    async attachLatestReminderDates (vehicles) {
      await Promise.all(vehicles.map(async (vehicle) => {
        const notifications = await this.loadNotificationsByVehicle(vehicle.vehicleId)
        vehicle.latestReminderDate = this.getLatestReminderDate(notifications)
      }))
    },

    groupNotificationsByVehicle (notifications) {
      const vehiclesById = {}

      notifications.forEach(notification => {
        const vehicleId = notification.vehicleId

        if (!vehicleId) {
          return
        }

        if (!vehiclesById[vehicleId]) {
          vehiclesById[vehicleId] = {
            vehicleId,
            notifications: []
          }
        }

        vehiclesById[vehicleId].notifications.push(notification)
      })

      return Object.values(vehiclesById)
    },

    splitVehiclesByReminderCooldown (vehicles) {
      const eligibleVehicles = []
      const skippedByCooldownVehicles = []

      vehicles.forEach(vehicle => {
        const inCooldown = this.isReminderDateInCooldown(vehicle.latestReminderDate)

        if (inCooldown) {
          skippedByCooldownVehicles.push(vehicle)
        } else {
          eligibleVehicles.push(vehicle)
        }
      })

      return {
        eligibleVehicles,
        skippedByCooldownVehicles
      }
    },

    getReminderRecipientLabel (vehicle, includeLatestReminderDate = false) {
      const notification = vehicle.notifications[0] || {}
      const label = notification.licenseNumber || notification.vinNumber || vehicle.vehicleId

      if (!includeLatestReminderDate || !vehicle.latestReminderDate) {
        return label
      }

      return `${label} (${dateTimeUtil.utcToLocalDisplayDateTimeFormat(vehicle.latestReminderDate)})`
    },

    getReminderRecipientsPreview (vehicles, previewLimit = 5, includeLatestReminderDate = false) {
      const recipients = vehicles
        .slice(0, previewLimit)
        .map(item => this.getReminderRecipientLabel(item, includeLatestReminderDate))
        .join(', ')

      return {
        recipients: recipients || '-',
        moreCount: Math.max(0, vehicles.length - previewLimit)
      }
    },

    getReminderConfirmMessage ({ vehicles, eligibleVehicles, skippedByCooldownVehicles }) {
      const willSendPreview = this.getReminderRecipientsPreview(eligibleVehicles)
      const skippedPreview = this.getReminderRecipientsPreview(skippedByCooldownVehicles, 5, true)

      const parts = [
        this.$t('notification.reminderSendConfirmSelected', { count: vehicles.length }),
        this.$t('notification.reminderSendConfirmWillSend', { count: eligibleVehicles.length }),
        this.$t('notification.reminderSendConfirmSkippedCooldown', {
          count: skippedByCooldownVehicles.length,
          days: reminderCooldownDays
        }),
        this.$t('notification.reminderSendConfirmChannels')
      ]

      if (eligibleVehicles.length > 0) {
        parts.push(this.$t('notification.reminderSendConfirmWillSendRecipients', { recipients: willSendPreview.recipients }))
      }

      if (skippedByCooldownVehicles.length > 0) {
        parts.push(this.$t('notification.reminderSendConfirmSkippedRecipients', {
          recipients: skippedPreview.recipients,
          days: reminderCooldownDays
        }))
      }

      return parts.join('<br/>')
    },

    openViewDialog (notification, tableRow) {
      const target = tableRow && tableRow.event && tableRow.event.target

      if (target && target.closest && target.closest('.v-data-table__checkbox, .v-input--selection-controls, .v-simple-checkbox')) {
        return
      }

      this.viewDialog = true
      this.viewDialog_notification = notification
    },

    async sendReminderSMS () {
      let reminderRequestStarted = false

      try {
        const selectedNotifications = this.selectedNotifications

        if (!selectedNotifications.length) {
          return
        }

        this.isSendingReminders = true
        const vehicles = this.groupNotificationsByVehicle(selectedNotifications)
        await this.attachLatestReminderDates(vehicles)
        const { eligibleVehicles, skippedByCooldownVehicles } = this.splitVehiclesByReminderCooldown(vehicles)
        this.isSendingReminders = false

        const confirm = await this.$confirm(
          this.getReminderConfirmMessage({
            vehicles,
            eligibleVehicles,
            skippedByCooldownVehicles
          }),
          {
            title: this.$t('notification.reminderSendConfirmTitle'),
            width: 560,
            trueText: this.$t('yes'),
            falseText: this.$t('cancel')
          }
        )

        if (confirm !== true) {
          return
        }

        const vehicleIds = eligibleVehicles.map(item => item.vehicleId)

        if (!vehicleIds.length) {
          this.showSnackbar(this.$t('notification.remindersSkippedCooldownOnly', { days: reminderCooldownDays }), 4000)
          return
        }

        this.isSendingReminders = true
        this.showSnackbarProcessing()
        reminderRequestStarted = true

        await this.sendNotificationsReminders(vehicleIds)

        const skippedCount = skippedByCooldownVehicles.length
        const successCount = vehicleIds.length

        if (!skippedCount) {
          this.showSnackbarSuccess(this.$t('notification.remindersSent', { count: successCount }))
        } else {
          this.showSnackbar(this.$t('notification.remindersSentWithSkippedCooldown', {
            successCount,
            skippedCount,
            days: reminderCooldownDays
          }), 5000)
        }
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      } finally {
        if (reminderRequestStarted) {
          await this.reloadTableItems()
        }

        this.selectedNotifications = []
        this.isSendingReminders = false
      }
    },

    onSearchSubmit () {
      this.reloadTableItems(1)
    },

    onPageInput () {
      this.reloadTableItems()
    },

    getSortParams () {
      const [sortBy, sortOrder] = this.sortQueryKey.split(':')
      return { sortBy, sortOrder }
    },

    async reloadTableItems (page, isInitial = false) {
      try {
        this.startPageLoading()

        if (page && page !== this.pageNumber) {
          this.pageNumber = page
        }

        const items = await this.loadNotificationsTableItems({
          chainId: this.chainId,
          dealerId: this.currentDealerId,
          pageNumber: this.pageNumber,
          pageSize: this.pageSize,
          search: this.search,
          ...this.getSortParams(),
          isInitial
        })

        this.selectedNotifications = []

        return items
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      } finally {
        this.stopPageLoading()
      }
    }
  },


  mounted () {
    this.restoreInitialNotificationsTableItems(this.currentDealerId)
  },


  async created () {
    try {
      this.setPageTitle(this.$t('nav.notifications'))
      this.startPageLoading()

      await this.loadNotificationsTableItems({
        chainId: this.chainId,
        dealerId: this.currentDealerId,
        pageNumber: this.pageNumber,
        pageSize: this.pageSize,
        ...this.getSortParams(),
        isInitial: true
      })
    } catch (error) {
      this.showSnackbarError()
      this.$log.error(error)
    } finally {
      this.stopPageLoading()
      this.$nextTick(() => {
        this.skipSortReload = false
      })
    }
  }
}
</script>
